import {
  AutocompleteNoOptions,
  Avatar,
  colors,
  DropdownItem,
  Icon,
  SkeletonText,
  Tag,
} from '@dev-spendesk/grapes';
import { Trans } from 'react-i18next';

import { AutocompleteSearch } from 'src/core/common/components/AutocompleteSearch';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useApprovalAccountOwnerRoleEnabled } from 'src/core/modules/app/hooks/useApprovalAccountOwnerRoleEnabled';
import { useMembersQuery } from 'src/core/modules/company/cost-centers/containers/CostCentersPageContainer/hooks/useMembersQuery';

import { type SelectedApprover } from '../../types';

type Props = {
  onSelect: (selectedApprover: SelectedApprover) => void;
  onBlur: () => void;
  currentApproverId?: string;
  className?: string;
};

export const SelectApproverDropdown = ({
  onSelect,
  onBlur,
  currentApproverId,
  className,
}: Props) => {
  const membersQueryState = useMembersQuery();
  const isReportingManagerFeatureEnabled = useFeature(
    FEATURES.REPORTING_MANAGERS,
  );
  const isApprovalAccountOwnerRoleEnabled =
    useApprovalAccountOwnerRoleEnabled();
  const { t } = useTranslation('global');

  return (
    <QuerySuspense
      queryState={membersQueryState}
      loading={<SkeletonText />}
      fallback={(error) => (
        <QueryError queryError={error} componentType="Callout" />
      )}
    >
      {(members) => {
        let options = members
          .filter((member) => member.id !== currentApproverId)
          .map((member) => ({
            key: member.id,
            label: member.isPending ? member.email : member.fullname,
            avatar: member.avatar,
            isCostCenterOwner: false,
            isReportingManager: false,
            isAccountOwnerType: false,
          }))
          .sort(
            (a, b) => Number(b.isCostCenterOwner) - Number(a.isCostCenterOwner),
          );

        if (isReportingManagerFeatureEnabled) {
          options = [
            {
              key: 'reportingManager',
              label: t('teams.approvalFlows.reportingManager'),
              avatar: '',
              isCostCenterOwner: false,
              isReportingManager: true,
              isAccountOwnerType: false,
            },
            ...options,
          ];
        }

        // Add cost center owner abstract role
        options = [
          {
            key: 'costCenterOwner',
            label: t('teams.approvalFlows.costCenterOwner'),
            avatar: '',
            isCostCenterOwner: true,
            isReportingManager: false,
            isAccountOwnerType: false,
          },
          ...options,
        ];

        if (isApprovalAccountOwnerRoleEnabled) {
          options = [
            {
              key: 'accountOwner',
              label: t('teams.approvalFlows.accountOwner'),
              avatar: '',
              isCostCenterOwner: false,
              isReportingManager: false,
              isAccountOwnerType: true,
            },
            ...options,
          ];
        }

        return (
          <AutocompleteSearch
            name="ownerId"
            className={className}
            fit="parent"
            // ref={searchReference} TODO: Implement ref for Autocomplete Grapes component
            options={options}
            isInputVisible
            isAutocompletePlace
            placeholder={t('teams.approvalFlows.changeApproverPlaceholder')}
            value={undefined}
            renderPrefix={() => <Icon name="magnifying-glass" />}
            onBlur={() => {
              onBlur();
            }}
            renderOption={(option, state) => {
              if (option.isCostCenterOwner) {
                return (
                  <DropdownItem
                    {...option}
                    {...state}
                    key={option.key}
                    label={
                      <div className="flex items-center gap-8">
                        <span className="text-content-primary title-m">
                          {t('teams.approvalFlows.costCenterOwner')}
                        </span>
                      </div>
                    }
                    prefix={
                      <div className="mt-4 flex h-24 w-24 items-center justify-center rounded-[50%] bg-background-secondary-brand-default">
                        <Icon
                          name="person"
                          color={colors.backgroundPrimaryDefault}
                        />
                      </div>
                    }
                  />
                );
              }
              if (option.isReportingManager) {
                return (
                  <DropdownItem
                    {...option}
                    {...state}
                    key={`${option.key}-${option.isReportingManager}}`}
                    label={
                      <div>
                        <div className="flex items-center gap-8">
                          <span className="text-content-primary title-m">
                            {t('teams.approvalFlows.reportingManager')}
                          </span>
                          <Tag variant="purple">{t('misc.new')}</Tag>
                        </div>
                        <div className="mt-4 text-content-primary body-s">
                          {t('teams.approvalFlows.reportingManagerDescription')}
                        </div>
                      </div>
                    }
                    prefix={
                      <div className="mt-4 flex h-24 w-24 items-center justify-center rounded-[50%] bg-background-secondary-brand-default">
                        <Icon
                          name="person"
                          color={colors.backgroundPrimaryDefault}
                        />
                      </div>
                    }
                  />
                );
              }
              if (option.isAccountOwnerType) {
                return (
                  <DropdownItem
                    {...option}
                    {...state}
                    key={`${option.key}-${option.isAccountOwnerType}}`}
                    label={
                      <div className="flex items-center gap-8 text-content-primary title-m">
                        {t('teams.approvalFlows.accountOwner')}
                      </div>
                    }
                    prefix={
                      <div className="mt-4 flex h-24 w-24 items-center justify-center rounded-[50%] bg-background-secondary-brand-default">
                        <Icon
                          name="person"
                          color={colors.backgroundPrimaryDefault}
                        />
                      </div>
                    }
                  />
                );
              }
              return (
                <DropdownItem
                  {...option}
                  {...state}
                  prefix={
                    <Avatar
                      size={24}
                      src={option.avatar ?? ''}
                      text={option.label}
                    />
                  }
                />
              );
            }}
            onSelect={(option) => {
              if (option) {
                if (option.isCostCenterOwner) {
                  onSelect({ type: 'costCenterOwner' });
                  return;
                }
                if (option.isReportingManager) {
                  onSelect({ type: 'reportingManager' });
                  return;
                }
                if (option.isAccountOwnerType) {
                  onSelect({ type: 'accountOwner' });
                  return;
                }
                onSelect({
                  id: option.key,
                  type: 'user',
                });
              }
            }}
            renderNoOptions={(rawValue) => (
              <AutocompleteNoOptions className="text-center body-m">
                <div>
                  <Trans
                    i18nKey="misc.noOptions"
                    values={{ account: rawValue }}
                    components={[
                      <span key="complementary" className="text-content-primary" />,
                    ]}
                  />
                </div>
              </AutocompleteNoOptions>
            )}
          />
        );
      }}
    </QuerySuspense>
  );
};
